/* ---------------------------------------------------------------------------
 * The observation window: one report per ATTEMPT, and none past the call.
 *
 * Two things close a window and neither may be dropped. Ordinarily the attempt
 * closes its own, when it settles. But a deadline can fire while an attempt is
 * still stuck, and that attempt never comes back to close anything -- so the
 * owner closes it instead, because a handler counting upstream traffic has to
 * hear about the call that timed out most of all.
 *
 * Sealing is the other half of the same problem. The loop that lost the race
 * keeps running afterwards, and an attempt reporting to a caller who was
 * answered seconds ago is a worse report than the one this fixes: an
 * over-report nobody can place against a call that is finished.
 * ------------------------------------------------------------------------ */

/**
 * The two ends of one observation window.
 *
 * @remarks
 * Both are required and neither has a default. A span whose callbacks are
 * optional keeps a variable nobody reads, and that shape should not compile.
 * Spreading this object is what wires both at once; picking a single key out of
 * it by hand still wires half the contract.
 */
export interface SpanObservers {
  /** Runs when a window closes, exactly once per open. */
  readonly onClose: () => void;

  /** Runs before a window opens, so the owner can clear what the last one left. */
  readonly onOpen: () => void;
}

/** One observation window at a time, over a run that may end before it does. */
export interface Span {
  /** Closes the open window, if one is open. Idempotent. */
  close: () => void;

  /** Opens a window, unless this span is already sealed. */
  open: () => void;

  /** Closes whatever is open, then refuses every later open. */
  seal: () => void;
}

/**
 * Builds a span over one run.
 *
 * @param observers - The two ends of the window. Both required, see
 * {@link SpanObservers}.
 * @returns A span that is closed until {@link Span.open} is called.
 */
export function span(observers: SpanObservers): Span {
  let observation: 'closed' | 'open' | 'over' = 'closed';

  /**
   * Opens the window for the attempt about to run, on a cleared outcome.
   *
   * The `over` check is a belt over braces and is NOT reached today. MEASURED
   * across the whole suite: zero hits. The braces are `RC47` -- everything that
   * ends a call (our deadline, the caller's signal) produces a failure the loop
   * will not repeat, so the loop stops on its own rather than coming back here
   * for another attempt. The check stays for the day that stops being true,
   * and this note is here so nobody mistakes it for tested ground.
   */
  const open = (): void => {
    if (observation === 'over') return;
    observers.onOpen();
    observation = 'open';
  };

  /** Fires the observers for the open attempt, if one is still open. */
  const close = (): void => {
    if (observation !== 'open') return;
    // Moved BEFORE the observer runs, so a `close()` from inside `onClose` is
    // the no-op the guard above promises rather than a second report.
    observation = 'closed';
    observers.onClose();
  };

  /**
   * Closes whatever is still open, and refuses everything after it.
   *
   * The order is the whole point and swapping it is the bug: marking the span
   * over first would make the close a no-op, and the attempt the deadline
   * outran would go unreported -- exactly the case this exists for.
   */
  const seal = (): void => {
    close();
    observation = 'over';
  };

  return { close, open, seal };
}
